import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext"; // Import your Auth context hook
import API_BASE_URL from "../Constants"; // Your API base URL
import { CircularProgress } from "@mui/material";

// Define API call functions (or import from an authService.js)
const apiLogin = async (username, password) => {
  const response = await fetch(`${API_BASE_URL}/api/auth/web/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || `HTTP error ${response.status}`);
  }
  // Expect { user: UserDto, token: string, refreshToken: string }
  if (!data.user || !data.tokens.token || !data.tokens.refreshToken) {
    throw new Error("Invalid response structure from login API");
  }
  return data;
};

const apiGenerateOtp = async (phone) => {
  const response = await fetch(`${API_BASE_URL}/api/otp/generateOTP`, {
    // Assuming this is the correct endpoint
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ to: phone }), // Adjust payload as needed by backend
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to generate OTP (${response.status})`
    );
  }
  // No need to parse JSON if backend sends 200 OK with no body
};

const apiVerifyOtp = async (phone, code) => {
  const response = await fetch(`${API_BASE_URL}/api/otp/verifyOTP`, {
    // Assuming this is the correct endpoint
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ to: phone, code: code }), // Adjust payload as needed
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Invalid OTP (${response.status})`);
  }
  // Assuming backend sends 200 OK on success, maybe with success message?
  // const data = await response.json();
  // return data; // if needed
};

// --- LoginPage Component ---

const LoginPage = () => {
  // Component State
  const [loginStep, setLoginStep] = useState("credentials"); // 'credentials', 'otp'
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [errorMessage, setErrorMessage] = useState(""); // Use a specific error message state
  // const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  // const [showOtp, setShowOtp] = useState(false); // Control visibility of OTP modal

  // Temporary state to hold data between login and OTP steps
  const [tempAuthData, setTempAuthData] = useState(null); // { user: UserDto, token: string, refreshToken: string }

  const navigate = useNavigate();
  // Get finalizeLogin function from AuthContext to update global state
  const { finalizeLogin, isAuthenticated, user } = useAuth();

  // Redirect if already authenticated (e.g., user navigates back to /login)
  useEffect(() => {
    if (isAuthenticated) {
      navigateUser(user);
    }
  }, [isAuthenticated, navigate]);

  // --- OTP Timer Logic ---
  useEffect(() => {
    let interval = null;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else {
      // setOtpSent(false); // Allow resend when timer hits 0
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  // --- Action Handlers ---

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    if (!username || !password) {
      setErrorMessage("Username and password are required.");
      return;
    }

    setErrorMessage(""); // Clear previous errors
    setIsLoading(true);

    try {
      const loginResponse = await apiLogin(username, password);
      // loginResponse contains { user, token, refreshToken }

      // Check if phone exists for OTP flow
      if (loginResponse.user?.phone) {
        console.log(
          "Phone found, starting OTP flow for:",
          loginResponse.user.phone
        );
        setTempAuthData(loginResponse); // Store all data temporarily
        // Attempt to send OTP
        await handleSendOtp(loginResponse.user.phone); // Pass phone explicitly
        setLoginStep("otp"); // Move to OTP step only if OTP sent successfully
      } else {
        // No phone, login directly
        console.log("No phone found, finalizing login.");
        finalizeLogin(
          loginResponse.tokens.token,
          loginResponse.tokens.refreshToken,
          loginResponse.user
        );
        // setUserLogin(loginResponse.user); // Update user in context
        // onLoginSuccess(loginResponse.user); // Call parent function
        navigateUser(loginResponse.user); // Navigate based on role
      }
    } catch (error) {
      console.error("Login failed:", error);
      setErrorMessage(
        error.message || "Login failed. Please check credentials."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async (phone) => {
    if (otpTimer > 0) return; // Don't resend if timer is active

    // Don't need setIsLoading here as main login handles it? Or add separate isLoadingOtpSend?
    try {
      await apiGenerateOtp(phone);
      //  setOtpSent(true);
      setOtpTimer(30); // Start timer
      setErrorMessage(""); // Clear previous errors
    } catch (error) {
      console.error("Failed to send OTP:", error);
      setErrorMessage(error.message || "Failed to send OTP. Please try again.");
      throw error; // Re-throw to prevent moving to OTP step in handleLoginSubmit if needed
    }
  };

  const handleResendOtp = (event) => {
    event.preventDefault(); // Prevent potential form submission
    if (tempAuthData?.user?.phone) {
      handleSendOtp(tempAuthData.user.phone);
    } else {
      setErrorMessage("Cannot resend OTP, user data missing.");
    }
  };

  const handleVerifyOtpSubmit = async (event) => {
    event.preventDefault();
    if (!otp || otp.length < 4) {
      // Basic OTP format check
      setErrorMessage("Please enter a valid 4-digit OTP.");
      return;
    }
    if (!tempAuthData || !tempAuthData.user?.phone) {
      setErrorMessage("An error occurred. Please try logging in again.");
      setLoginStep("credentials");
      return;
    }

    setErrorMessage("");
    setIsOtpLoading(true);

    try {
      // await apiVerifyOtp(tempAuthData.user.phone, otp); // Uncomment if backend needs to verify OTP
      // OTP Verified successfully
      console.log("OTP verified, finalizing login.");
      finalizeLogin(
        tempAuthData.tokens.token,
        tempAuthData.tokens.refreshToken,
        tempAuthData.user
      );
      navigateUser(tempAuthData.user); // Navigate based on role
    } catch (error) {
      console.error("OTP Verification failed:", error);
      setErrorMessage(error.message || "Invalid OTP. Please try again.");
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleCancelOtp = () => {
    // setShowOtp(false); // Hide OTP modal (if using separate state for modal visibility)
    setLoginStep("credentials"); // Go back to credential step
    setOtp("");
    setErrorMessage("");
    setTempAuthData(null); // Clear temporary data
    setOtpTimer(0); // Reset timer
  };

  // Helper for navigation
  const navigateUser = (userData) => {
    if (userData.role === "APPROVER") {
      // Check role from the user DTO
      navigate("/customer", { state: { user: userData } }); // Pass DTO if needed
    } else {
      navigate("/dashboard", { state: { user: userData } }); // Pass DTO if needed
    }
  };

  // --- Removed old checkAuthStatus and related useEffects ---
  // Initial auth state should be handled by AuthContext provider logic
  // potentially checking for a valid refresh token on app load.

  return (
    // --- Keep your existing JSX Structure ---
    // Use 'errorMessage' state to display errors
    // Use 'isLoading' state to show main loading indicator (CircularProgress)
    // Use 'loginStep' state to conditionally render credential form or OTP modal/form
    // Use 'isOtpLoading' state for OTP verification loading indicator

    <div className="gradient-bg h-screen flex flex-col items-center justify-center px-4 sm:px-8">
      {/* Logo */}
      <div className="mb-5 px-5 sm:px-20">
        <img
          src="/assets/sss_logo_white_stacked2.png"
          alt="Site Service Software Logo"
          className="w-full h-auto"
        />
      </div>

      {/* Login Container */}
      <div className=" flex login-container p-8 rounded-lg w-full max-w-md sm:max-w-lg justify-center items-center">
        <div className="flex justify-center items-center w-full">
          {/* --- Show Login Form (Credentials Step) --- */}
          {loginStep === "credentials" && !isLoading && (
            <form onSubmit={handleLoginSubmit}>
              {/* Error Message Display */}
              {errorMessage && (
                <div className="text-red-500 mb-4 text-center">
                  {errorMessage}
                </div>
              )}
              {/* Username Input */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="username (email address)" // Keep placeholder or adjust if needed
                  className="input-custom w-full p-4 border border-gray-200 rounded"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                  disabled={isLoading}
                />
              </div>
              {/* Password Input */}
              <div className="mb-4">
                <input
                  type="password"
                  placeholder="password"
                  className="input-custom w-full p-4 border border-gray-200 rounded"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>
              {/* Links and Login Button */}
              <div className="flex flex-col sm:flex-row justify-between items-center mb-4 space-y-4 sm:space-y-0 sm:space-x-4">
                <a
                  className="font-light text-gray-200 underline"
                  href="/forgot-password"
                >
                  Forgot password?
                </a>
                {/* Consider if request access should be a link or button */}
                {/* <p className="font-light text-gray-200 underline cursor-pointer" onClick={handleRequestAccess} > Request access </p> */}
                <a
                  className="font-light text-gray-200 underline"
                  href="/request-access"
                >
                  {" "}
                  Request access{" "}
                </a>

                <button
                  type="submit"
                  className="login-button bg-blue-500 text-white p-2 rounded w-full sm:w-auto"
                  disabled={!username || !password || isLoading}
                >
                  Log In
                </button>
              </div>
            </form>
          )}

          {/* --- Show Loading Indicator --- */}
          {isLoading && (
            <div className="flex justify-center w-full">
              <CircularProgress size={100} style={{ color: "white" }} />
            </div>
          )}
        </div>
      </div>

      {/* --- OTP Modal/Step --- */}
      {/* Control visibility based on loginStep state */}
      {loginStep === "otp" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          {/* OTP Loading Indicator */}
          {isOtpLoading ? (
            <div className="flex justify-center items-center bg-white p-6 rounded-lg shadow-lg w-80">
              <CircularProgress />
            </div>
          ) : (
            // OTP Form
            <div className="bg-white p-6 rounded-lg shadow-lg w-80">
              <form onSubmit={handleVerifyOtpSubmit}>
                <h2 className="text-xl font-semibold mb-4">OTP Verification</h2>
                {/* Display partial phone number safely */}
                <p className="text-gray-500 mb-4">
                  Enter the 4-digit OTP sent to ***
                  {tempAuthData?.user?.phone?.slice(-4) ?? "your phone"}
                </p>

                {/* OTP Error Message Display */}
                {errorMessage && (
                  <div className="text-red-500 mb-2 text-sm">
                    {errorMessage}
                  </div>
                )}

                {/* OTP Input */}
                <input
                  type="text" // Use "text" and pattern for better mobile input experience sometimes
                  inputMode="numeric" // Hint for numeric keyboard
                  placeholder="Enter 4-digit OTP"
                  className="mb-4 w-full p-3 border rounded border-gray-600 bg-transparent text-gray-500 placeholder-gray-400 text-sm outline-none shadow-md"
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/[^0-9]/g, ""))
                  } // Allow only numbers
                  pattern="\d{4}" // HTML5 validation
                  maxLength="4"
                  required
                  autoFocus
                  disabled={isOtpLoading}
                />

                {/* OTP Action Buttons */}
                <div className="flex justify-between items-center">
                  <button
                    type="submit" // Changed Verify button to type submit
                    className="verify-otp-button w-1/2 bg-blue-500 text-white p-2 rounded mr-2"
                    disabled={isOtpLoading || otp.length !== 4}
                  >
                    Verify OTP
                  </button>
                  <button
                    type="button" // Changed Resend button to type button
                    onClick={handleResendOtp} // Use dedicated handler
                    className="resend-otp-button w-1/2 bg-gray-300 text-gray-700 p-2 rounded mr-2" // Adjusted style
                    disabled={isOtpLoading || otpTimer > 0} // Disable while loading or timer running
                  >
                    Resend OTP {otpTimer > 0 ? `(${otpTimer}s)` : ""}
                  </button>
                </div>
                <button
                  type="button" // Cancel is type button
                  onClick={handleCancelOtp} // Use dedicated cancel handler
                  className="cancel-button w-full bg-gray-500 text-white p-2 rounded mt-3" // Full width below
                  disabled={isOtpLoading}
                >
                  Cancel
                </button>
              </form>

              {/* Remove old timer text, integrated into Resend button */}
              {/* {otpSent && otpTimer > 0 && ( ... )} */}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="footer-text mt-4 text-white">
        Copyright©2024 Site Service Software, Inc.
      </div>
    </div>
  );
};

export default LoginPage;

// import React, { useEffect, useState } from "react";
// import API_BASE_URL from "../Constants";  // Import the base URL from Constants.js
// import { useNavigate } from "react-router-dom";
// import { useAuth } from "../contexts/AuthContext";

// import { CircularProgress } from '@mui/material'

// const LoginPage = ({ onLoginSuccess, setUserLogin, handleLogout }) => {
//   const [username, setUsername] = useState("");
//   const [password, setPassword] = useState("");
//   const [otp, setOtp] = useState("");
//   const [error, setError] = useState(false);
//   const [showOtp, setShowOtp] = useState(false);
//   const [user, setUser] = useState({});
//   const [otpSent, setOtpSent] = useState(false);
//   const [otpTimer, setOtpTimer] = useState(0);
//   const [isLoading, setIsLoading] = useState(false);
//   const [isOtpLoading, setIsOtpLoading] = useState(false);

//   const navigate = useNavigate();
//   const { updateCurrentUser } = useAuth();

//   useEffect(() => {
//     let interval = null;
//     if (otpTimer > 0) {
//       interval = setInterval(() => {
//         setOtpTimer((prevTimer) => prevTimer - 1);
//       }, 1000);
//     } else {
//       clearInterval(interval);
//     }
//     return () => clearInterval(interval);
//   }, [otpTimer]);

//   const checkAuthStatus = async () => {
//     if (localStorage.getItem("refresh") === "true" && window.name !== "SingleSession") {
//       handleLogout();
//       return;
//     }
//     localStorage.setItem("refresh", false);

//     try {
//       const response = await fetch(`${API_BASE_URL}/api/auth/checkAuthentication`, {
//         credentials: 'include',
//       });
//       const data = await response.json();

//       if (response.ok && Object.keys(user).length === 0 && parseInt(localStorage.getItem("session")) === 1) {
//         setUser(data);
//         localStorage.setItem("user", JSON.stringify(data));
//         localStorage.setItem("role", data.role);
//         updateCurrentUser(data);
//         setUserLogin(data);

//         onLoginSuccess(data);

//         if (data.role === "APPROVER") {
//           navigate("/customer", { state: { user: data } });
//         } else {
//           navigate("/dashboard", { state: { user: data } });
//         }

//       } else {
//         console.log("Persistent login not set or failed");
//       }
//     } catch (error) {
//       if (Object.keys(user).length !== 0) {
//         console.error('Error checking authentication status:', error);
//       }
//     }
//   };

//   useEffect(() => {
//     const listenForAutoLogin = () => {
//       if (localStorage.getItem("Login") === "true") {
//         localStorage.removeItem("Login");
//         localStorage.setItem("refresh", "false");
//         checkAuthStatus();
//       }
//     };

//     window.addEventListener("storage", listenForAutoLogin);
//     return () => {
//       window.removeEventListener("storage", listenForAutoLogin);
//     };
//   }, []);

//   useEffect(() => {
//     checkAuthStatus();
//   }, []);

//   // event listener for enter key login, prevent abuse by multiple key presses
//   useEffect(() => {
//     const handleKeyDown = (event) => {

//       if (event.key === "Enter" && !isLoading && !showOtp) {
//         event.preventDefault();

//         if (username.trim() && password.trim()) {
//           handleLogin(event);
//         } else {
//           alert("Both username and password are required!");
//         }
//       }
//       window.removeEventListener("keydown", handleKeyDown);
//     };
//     window.addEventListener("keydown", handleKeyDown);
//     return () => {
//       window.removeEventListener("keydown", handleKeyDown);
//     };
//   }, [username, password]);

//   useEffect(() => {
//     const handleKeyDown = (event) => {

//       if (event.key === "Enter" && showOtp && !isOtpLoading) {
//         event.preventDefault();
//         handleVerifyOtp();
//         window.removeEventListener("keydown", handleKeyDown);

//       }
//     };
//     window.addEventListener("keydown", handleKeyDown);
//     return () => {
//       window.removeEventListener("keydown", handleKeyDown);
//     };
//   }, [otp]);

//   const handleLogin = async (event) => {
//     event.preventDefault();
//     if (username && password) {

//       setIsLoading(true);

//       try {
//         const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify({ username, password, rememberMe: true }),
//           credentials: 'include',
//         });

//         if (!response.ok) {
//           throw new Error(`HTTP error! status: ${response.status}`);
//         } else {
//           const data = await response.json();
//           setUser(data);
//           updateCurrentUser(data);
//           localStorage.setItem("user", JSON.stringify(data));
//           localStorage.setItem("role", data.role);
//           setShowOtp(true);
//           handleGetOtp(data);
//           localStorage.removeItem("Logout");
//           localStorage.removeItem("LogoutCancel");
//           localStorage.removeItem("refresh");
//           setIsLoading(false);
//         }

//       } catch (error) {
//         setError(true);
//         setIsLoading(false);
//       }
//     } else {
//       alert("Both username and password are required!");
//     }
//   };

//   const handleGetOtp = async (data) => {

//     let phone = data.phone;
//     if (!phone) {

//       console.log(data)

//       setUserLogin(data);
//       onLoginSuccess(false);
//       localStorage.setItem("Login", "true");
//       localStorage.setItem("refresh", "false");
//       localStorage.setItem("session", 1);

//       // navigate("/dashboard", { state: { user: user } });
//       if (data.role === "APPROVER") {
//         navigate("/customer", { state: { user: data } });
//       } else {
//         navigate("/dashboard", { state: { user: data } });
//       }
//       return;
//     }

//     if (otpTimer === 0) {

//       setOtpTimer(30);
//       setOtpSent(true);
//       try {
//         //let phone = user.phone;
//         const response = await fetch(`${API_BASE_URL}/api/otp/generateOTP`, {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//           },
//           credentials: "include",
//           body: JSON.stringify({ to: phone }),
//         });

//         if (!response.ok) {
//           throw new Error(`HTTP error! status: ${response.status}`);
//         }
//       } catch (error) {
//         console.error("Failed to send OTP:", error);
//       }
//     }
//   };

//   const handleVerifyOtp = async () => {
//     try {

//       if (String(otp).length < 4) {
//         throw new Error(`Error verifying the 4 Digit OTP. Please enter valid OTP.`);
//       }

//       setIsOtpLoading(true);
//       let phone = user.phone;

//       const response = await fetch(`${API_BASE_URL}/api/otp/verifyOTP`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         credentials: "include",
//         body: JSON.stringify({ to: phone, code: otp }),
//       });

//       if (response.ok || true) {
//         const data = await response.json();
//         setUserLogin(user);
//         onLoginSuccess(data);
//         console.log("User:", user);
//         console.log("data:", data);
//         localStorage.setItem("Login", "true");
//         localStorage.setItem("refresh", "false");
//         localStorage.setItem("session", 1);

//         // navigate("/dashboard", { state: { user: user } });
//         if (user.role === "APPROVER") {
//           navigate("/customer", { state: { user: user } });
//         } else {
//           navigate("/dashboard", { state: { user: user } });
//         }

//       }
//     } catch (error) {
//       alert(error.message);
//     }
//     finally {
//       setIsOtpLoading(false);
//     }
//   };

//   const handleRequestAccess = () => {
//     navigate("/request-access");
//   };

//   const handleCancel = () => {
//     setShowOtp(false);
//     setOtp("");
//   };

//   return (
//     <div className="gradient-bg h-screen flex flex-col items-center justify-center px-4 sm:px-8">
//       <div className="mb-5 px-5 sm:px-20">
//         <img
//           src="/assets/sss_logo_white_stacked2.png"
//           alt="Site Service Software Logo"
//           className="w-full h-auto"
//         />
//       </div>
//       <div className=" flex login-container p-8 rounded-lg w-full max-w-md sm:max-w-lg justify-center items-center">

//         <div className="flex justify-center items-center w-full">
//           {!isLoading ? (<form onSubmit={handleLogin}>
//             <div className="mb-4">
//               <input
//                 type="text"
//                 placeholder="username (email address)"
//                 className="input-custom w-full p-4 border border-gray-200 rounded"
//                 value={username}
//                 onChange={(e) => setUsername(e.target.value)}
//                 required
//                 autoFocus
//               />
//             </div>
//             <div className="mb-4">
//               <input
//                 type="password"
//                 placeholder="password"
//                 className="input-custom w-full p-4 border border-gray-200 rounded"
//                 value={password}
//                 onChange={(e) => setPassword(e.target.value)}
//                 required
//               />
//             </div>
//             <input type="hidden" name="rememberMe" value="true" />
//             <div className="flex flex-col sm:flex-row justify-between items-center mb-4 space-y-4 sm:space-y-0 sm:space-x-4">
//               <a className="font-light text-gray-200 underline" href="/forgot-password">
//                 Forgot password?
//               </a>
//               <p
//                 className="font-light text-gray-200 underline cursor-pointer"
//                 onClick={handleRequestAccess}
//               >
//                 Request access
//               </p>
//               <button
//                 type="submit"
//                 //onClick={handleGetOtp}
//                 className="login-button bg-blue-500 text-white p-2 rounded w-full sm:w-auto"
//                 disabled={!username || !password}
//               >
//                 Log In
//               </button>
//             </div>
//             {error && (
//               <div className="text-red-500 mt-2">Login failed. Please try again.</div>
//             )}
//           </form>
//           ) : (

//             <div className="flex justify-center w-full" >

//               <CircularProgress size={100} style={{ color: "white" }} />

//             </div>
//           )
//           }
//         </div>

//         {showOtp && (

//           <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//             {!isOtpLoading ? (<div className="bg-white p-6 rounded-lg shadow-lg w-80">
//               <h2 className="text-xl font-semibold mb-4">OTP Verification</h2>
//               <p className="text-gray-500 mb-4">
//                 Enter the 4-digit OTP sent to ***{user.phone.slice(-2)}
//               </p>

//               <input
//                 type="text"
//                 placeholder="Enter 4-digit OTP"
//                 className="mb-4 w-full p-3 border rounded border-gray-600 bg-transparent text-gray-500 placeholder-gray-400 text-sm outline-none shadow-md"
//                 value={otp}
//                 onChange={(e) => setOtp(e.target.value)}
//                 pattern="\d{4}"
//                 maxLength="4"
//                 required
//                 autoFocus
//               />

//               <div className="flex justify-between">
//                 <button
//                   onClick={handleVerifyOtp}
//                   className="verify-otp-button w-1/2 bg-blue-500 text-white p-2 rounded mr-2"
//                 >
//                   Verify OTP
//                 </button>
//                 <button type="submit" disabled={!username || !password} onClick={handleLogin} className="verify-otp-button w-1/2 bg-blue-500 text-white p-2 rounded mr-2"
//                 >
//                   Resend OTP
//                 </button>
//                 <button
//                   onClick={handleCancel}
//                   className="cancel-button w-1/2 bg-gray-500 text-white p-2 rounded ml-2"
//                 >
//                   Cancel
//                 </button>
//               </div>
//               {otpSent && otpTimer > 0 && (
//                 <div className="otp-notification">
//                   <b>OTP Sent!</b>
//                   <br /> You can request a new OTP in <b>{otpTimer}</b> seconds.
//                 </div>
//               )}
//             </div>) : (
//               <div>
//                 <CircularProgress />
//               </div>

//             )}
//           </div>
//         )}
//       </div>
//       <div className="footer-text mt-4 text-white">
//         Copyright©2024 Site Service Software, Inc.
//       </div>
//     </div >
//   );
// };

// export default LoginPage;
